import { useCallback } from "react";
import { stringToHex } from "viem";
import { useSignMessage } from "wagmi";

const PERSONAL_SIGN_METHOD = "personal_sign";

type Eip1193Provider = {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
};

type WalletConnector = {
  getProvider?: () => Promise<unknown> | unknown;
};

type WagmiSignMessageAsync = (args: { message: string }) => Promise<string>;

type SafeSignMessageArgs = {
  address?: string;
  connector?: WalletConnector;
  message: string;
};

type SignMessageWithConnectorArgs = SafeSignMessageArgs & {
  fallbackSignMessageAsync: WagmiSignMessageAsync;
};

const isEip1193Provider = (provider: unknown): provider is Eip1193Provider => {
  return (
    provider !== null &&
    provider !== undefined &&
    typeof provider === "object" &&
    "request" in provider &&
    typeof (provider as Eip1193Provider).request === "function"
  );
};

const getConnectorProvider = async (
  connector?: WalletConnector
): Promise<Eip1193Provider | null> => {
  if (!connector?.getProvider) return null;

  const provider = await connector.getProvider();
  return isEip1193Provider(provider) ? provider : null;
};

export const signMessageWithProvider = async ({
  address,
  message,
  provider,
}: SafeSignMessageArgs & {
  provider: Eip1193Provider;
}): Promise<string> => {
  if (!address) {
    throw new Error("Wallet address is required to sign the message.");
  }

  const signature = await provider.request({
    method: PERSONAL_SIGN_METHOD,
    params: [stringToHex(message), address],
  });

  if (typeof signature !== "string" || !signature) {
    throw new Error("Wallet returned an invalid message signature.");
  }

  return signature;
};

export const signMessageWithConnector = async ({
  address,
  connector,
  fallbackSignMessageAsync,
  message,
}: SignMessageWithConnectorArgs): Promise<string> => {
  const provider = await getConnectorProvider(connector);

  if (!provider) {
    return fallbackSignMessageAsync({ message });
  }

  return signMessageWithProvider({ address, message, provider });
};

export const useSafeSignMessage = () => {
  const { signMessageAsync, ...state } = useSignMessage();

  const safeSignMessageAsync = useCallback(
    (args: SafeSignMessageArgs) =>
      signMessageWithConnector({
        ...args,
        fallbackSignMessageAsync: signMessageAsync,
      }),
    [signMessageAsync]
  );

  return {
    ...state,
    signMessageAsync: safeSignMessageAsync,
  };
};
